// Marketers Marketplace — two-category job/gig board.
//   • "Looking for Content Creators" (advertisers post)
//   • "Available for Hire" (creators advertise themselves)
// Public read; authed write. Plugged into the global nav via /marketplace.
// Every post also drops onto the author's own profile newsfeed (kind: "marketplace"),
// sorted by timestamp like any other post — there's no separate global feed on this
// platform beyond individual profiles + this dedicated tab, so this covers both.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Mic, Plus, Trash2, ExternalLink, X, Upload, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const TABS = [
  { id: "looking_for_creators", label: "Looking for Content Creators", icon: Megaphone,
    blurb: "Advertisers, agencies & brands posting briefs for creators." },
  { id: "available_for_hire",  label: "Available for Hire",            icon: Mic,
    blurb: "Creators, streamers & influencers pitching their services." },
];

function applyByLabel(p) {
  if (!p.apply_by) return null;
  const d = new Date(p.apply_by);
  return p.is_open
    ? `Apply by ${d.toLocaleDateString()}`
    : `Closed ${d.toLocaleDateString()}`;
}

export default function Marketplace() {
  const { user } = useAuth();
  const [tab, setTab] = useState(TABS[0].id);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/posts", { params: { category: tab, limit: 80 } });
      setPosts(data);
    } catch { setPosts([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/marketplace/posts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="container-nexus py-8 space-y-6" data-testid="marketplace">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-2 text-nexus-cyan">// marketers marketplace</div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Briefs &amp; pitches.</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">Anyone on NEXUS can post a brief or pitch themselves. Replies go straight to your inbox — confidential, never public. Take negotiations off-platform via the contact handle if you'd like — payment flows through NX or external as agreed.</p>
        </div>
        {user && (
          <button onClick={() => setComposeOpen(true)} className="btn-nexus" data-testid="marketplace-compose">
            <Plus className="w-4 h-4" /> Post
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id} onClick={() => setTab(t.id)}
            data-testid={`mp-tab-${t.id}`}
            className={`px-4 py-2 text-[10px] tracking-[0.24em] uppercase font-display flex items-center gap-2 ${tab === t.id ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-white/40 hover:text-white"}`}
          >
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-white/50">{TABS.find((t) => t.id === tab)?.blurb}</p>

      {loading ? (
        <div className="text-center text-white/40 text-sm py-12">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="border border-white/10 bg-nexus-surface p-12 text-center space-y-2" data-testid="mp-empty">
          <div className="text-white/50 text-sm">No posts in this category yet.</div>
          {user && <div className="text-[11px] text-white/30">Be the first — click "Post".</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {posts.map((p) => (
            <div key={p.id} className={`border bg-nexus-surface p-4 space-y-3 transition-colors ${p.is_open === false ? "border-white/5 opacity-60" : "border-white/10 hover:border-nexus-cyan/40"}`} data-testid={`mp-post-${p.id}`}>
              <div className="flex items-start gap-3">
                <img src={p.author_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={`/u/${p.author_id}`} className="text-sm font-display font-bold truncate hover:text-nexus-cyan">{p.author_name}</Link>
                    {p.is_sponsored_author && <span className="text-[9px] tracking-[0.3em] text-nexus-amber">★ SPONSORED</span>}
                  </div>
                  <div className="text-[10px] text-white/40 tracking-wider">{new Date(p.created_at).toLocaleString()}</div>
                </div>
                {user && (user.id === p.author_id || user.role === "owner") && (
                  <button onClick={() => remove(p.id)} className="text-white/40 hover:text-nexus-crimson" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {p.image_url && (
                <img src={p.image_url} alt="" className="w-full max-h-56 object-cover rounded" data-testid={`mp-image-${p.id}`} />
              )}
              <h3 className="font-display font-bold text-base">{p.title}</h3>
              <p className="text-xs text-white/70 leading-relaxed whitespace-pre-wrap">{p.body}</p>
              <div className="flex flex-wrap gap-1.5">
                {(p.tags || []).map((tag) => (
                  <span key={tag} className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border border-white/10 text-white/40">#{tag}</span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/5 text-[11px]">
                {p.budget_nx && <span className="text-nexus-cyan">Budget: {p.budget_nx.toLocaleString()} NX</span>}
                {p.rate_nx_per_hour && <span className="text-nexus-mint">Rate: {p.rate_nx_per_hour.toLocaleString()} NX / hr</span>}
                {p.contact_handle && <span className="text-white/60">Contact: <code>{p.contact_handle}</code></span>}
                {p.portfolio_url && <a href={p.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-nexus-amber inline-flex items-center gap-1 hover:underline">portfolio <ExternalLink className="w-3 h-3" /></a>}
                {applyByLabel(p) && (
                  <span className={`inline-flex items-center gap-1 ${p.is_open ? "text-white/50" : "text-nexus-crimson"}`}>
                    <Clock className="w-3 h-3" /> {applyByLabel(p)}
                  </span>
                )}
              </div>
              {user && user.id !== p.author_id && p.is_open !== false && (
                replyingTo === p.id ? (
                  <ReplyBox postId={p.id} onDone={() => setReplyingTo(null)} />
                ) : (
                  <button onClick={() => setReplyingTo(p.id)} className="btn-ghost !py-1.5 !px-3 text-[10px]" data-testid={`mp-reply-btn-${p.id}`}>
                    <Send className="w-3 h-3" /> Reply privately
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {composeOpen && <ComposeModal category={tab} onClose={() => setComposeOpen(false)} onPosted={() => { setComposeOpen(false); load(); }} />}
    </div>
  );
}

function ReplyBox({ postId, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) { toast.error("Write a reply first"); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/posts/${postId}/reply`, { text: text.trim() });
      toast.success("Sent to their inbox");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
    setBusy(false);
  };
  return (
    <div className="flex gap-2" data-testid={`mp-reply-box-${postId}`}>
      <input
        value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
        placeholder="Reply privately — only they'll see this"
        className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-xs"
        data-testid={`mp-reply-input-${postId}`}
        onKeyDown={(e) => { if (e.key === "Enter") send(); }}
      />
      <button onClick={send} disabled={busy} className="btn-nexus !px-3" data-testid={`mp-reply-send-${postId}`}>
        {busy ? "…" : <Send className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function ComposeModal({ category, onClose, onPosted }) {
  const [form, setForm] = useState({
    title: "", body: "", budget_nx: "", rate_nx_per_hour: "", tags: "",
    contact_handle: "", portfolio_url: "", image_url: "", apply_by: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      setForm((cur) => ({ ...cur, image_url: url }));
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    setSubmitting(true);
    try {
      await api.post("/marketplace/posts", {
        category,
        title: form.title.trim(),
        body: form.body.trim(),
        budget_nx: form.budget_nx ? Number(form.budget_nx) : undefined,
        rate_nx_per_hour: form.rate_nx_per_hour ? Number(form.rate_nx_per_hour) : undefined,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        contact_handle: form.contact_handle || undefined,
        portfolio_url: form.portfolio_url || undefined,
        image_url: form.image_url || undefined,
        apply_by: form.apply_by ? new Date(form.apply_by).toISOString() : undefined,
      });
      toast.success("Posted — it's now live on your profile feed and this tab");
      onPosted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-nexus-surface border border-nexus-cyan/40 max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mp-compose-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-nexus-cyan">// new post · {category.replace(/_/g, " ")}</div>
            <h3 className="font-display font-black text-2xl tracking-tight mt-1">Create</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="mp-title" />
        <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Describe the brief or pitch in detail." rows={5} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="mp-body" />

        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/40">Cover image (optional)</label>
          <div className="mt-1 flex gap-2">
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://… or upload a file" className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="mp-image-url" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ghost !px-3" data-testid="mp-image-upload">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "…" : "Upload"}
            </button>
          </div>
        </div>

        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/40">Apply/reply by (optional — leave blank for no deadline)</label>
          <input
            type="datetime-local" value={form.apply_by} onChange={(e) => setForm({ ...form, apply_by: e.target.value })}
            className="mt-1 w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="mp-apply-by"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {category === "looking_for_creators" ? (
            <input value={form.budget_nx} onChange={(e) => setForm({ ...form, budget_nx: e.target.value })} placeholder="Budget (NX)" className="bg-transparent border border-white/10 px-3 py-2 text-sm" type="number" min="0" data-testid="mp-budget" />
          ) : (
            <input value={form.rate_nx_per_hour} onChange={(e) => setForm({ ...form, rate_nx_per_hour: e.target.value })} placeholder="Rate (NX / hr)" className="bg-transparent border border-white/10 px-3 py-2 text-sm" type="number" min="0" data-testid="mp-rate" />
          )}
          <input value={form.contact_handle} onChange={(e) => setForm({ ...form, contact_handle: e.target.value })} placeholder="Contact handle (discord, telegram)" className="bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="mp-contact" />
        </div>
        <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma-separated, e.g. gaming, fps, vlog)" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="mp-tags" />
        <input value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} placeholder="Portfolio / sample URL (optional)" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="mp-portfolio" />
        <button onClick={submit} disabled={submitting} className="btn-nexus w-full justify-center" data-testid="mp-submit">{submitting ? "…" : "Post"}</button>
      </div>
    </div>
  );
}
